import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSiteSettings, pick } from '@/contexts/SiteSettingsContext';
import { Phone, Mail, MapPin } from 'lucide-react';

const LOGO_URL = "/brand/guleed-logo-dark.png";

// Inline brand SVGs (lucide v1 removed brand icons)
const BRAND_ICONS = {
  linkedin: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5V9h3zM6.5 7.7A1.7 1.7 0 116.5 4a1.7 1.7 0 010 3.4zM19 19h-3v-5.1c0-1.4-.5-2.3-1.7-2.3a1.8 1.8 0 00-1.7 1.2 2.3 2.3 0 00-.1.8V19h-3V9h3v1.3A3 3 0 0115.2 9c2 0 3.8 1.3 3.8 4.1V19z"/></svg>),
  instagram: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><rect x="3" y="3" width="18" height="18" rx="5"/><path d="M16 11.4A4 4 0 1112.6 8 4 4 0 0116 11.4zM17.5 6.5h.01"/></svg>),
  facebook: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M22 12a10 10 0 10-11.6 9.9V15H8v-3h2.4V9.8c0-2.4 1.4-3.7 3.6-3.7a15 15 0 012.1.2v2.3h-1.2c-1.2 0-1.5.7-1.5 1.5V12h2.6l-.4 3h-2.2v7A10 10 0 0022 12z"/></svg>),
  twitter: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>),
  tiktok: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M19.6 6.3a5.9 5.9 0 01-3.4-1.1 5.9 5.9 0 01-2.3-3.7h-3v13.1a2.8 2.8 0 11-2-2.7V8.8a5.8 5.8 0 105 5.7V9.8a9 9 0 005.7 2V8.7a5.9 5.9 0 01-0-.03z"/></svg>),
  youtube: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M23 7.1a3 3 0 00-2.1-2.1C19 4.5 12 4.5 12 4.5s-7 0-8.9.5A3 3 0 001 7.1 31 31 0 00.5 12 31 31 0 001 16.9 3 3 0 003.1 19c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 002.1-2.1A31 31 0 0023.5 12 31 31 0 0023 7.1zM9.75 15.5v-7l6 3.5z"/></svg>),
};

const SOCIAL_META = [
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'twitter', label: 'X' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'youtube', label: 'YouTube' },
];

export default function Footer() {
  const { t, lang } = useLanguage();
  const { settings } = useSiteSettings();
  const font = lang === 'ar' ? 'var(--font-ar-body)' : 'var(--font-en-body)';
  const isAr = lang === 'ar';

  const phone = pick(settings?.phone, '+971 52 289 8249');
  const email = pick(settings?.email, 'info@gmreae.com');
  const address = pick(isAr ? settings?.address_ar : settings?.address_en, t('contact', 'address'));
  const tagline = pick(isAr ? settings?.content?.footer_tagline_ar : settings?.content?.footer_tagline_en, t('footer', 'tagline'));
  const social = settings?.social_links || {};

  return (
    <footer data-testid="site-footer" className="bg-[#071E2C] text-white/80" style={{ fontFamily: font }}>
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <img src={LOGO_URL} alt="Guleed Mortgage" className="h-12 mb-4" />
            <p className="text-white/50 text-sm leading-relaxed mb-4">{tagline}</p>
            <div className="flex items-center gap-3 flex-wrap">
              {SOCIAL_META.map(({ key, label }) => {
                const url = (social[key] || '').trim();
                if (!url) return null;
                return (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={label}
                    data-testid={`footer-social-${key}`}
                    className="w-9 h-9 rounded-lg bg-white/5 hover:bg-[#C9A84C]/20 border border-white/10 hover:border-[#C9A84C]/50 flex items-center justify-center text-white/60 hover:text-[#C9A84C] transition-colors">
                    {BRAND_ICONS[key]}
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              {isAr ? 'روابط سريعة' : 'Quick Links'}
            </h4>
            <div className="space-y-2">
              {[
                { to: '/', label: t('nav', 'home') },
                { to: '/about', label: t('nav', 'about') },
                { to: '/services', label: t('nav', 'services') },
                { to: '/digital-assets', label: isAr ? 'العملات الرقمية' : 'Crypto to Property' },
                { to: '/rates', label: t('nav', 'rates') },
                { to: '/blog', label: isAr ? 'المدونة' : 'Blog' },
                { to: '/referral', label: isAr ? 'برنامج الإحالة' : 'Referral Program' },
                { to: '/contact', label: t('nav', 'contact') },
              ].map(link => (
                <Link key={link.to} to={link.to} className="block text-sm text-white/50 hover:text-[#C9A84C] transition-colors">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              {t('services', 'title')}
            </h4>
            <div className="space-y-2 text-sm">
              {[
                { label: t('services', 'tab1'), tab: 'first' },
                { label: t('services', 'tab2'), tab: 'invest' },
                { label: t('services', 'tab3'), tab: 'nonres' },
                { label: t('services', 'tab4'), tab: 'refi' },
                { label: t('services', 'tab6'), tab: 'commercial' },
              ].map((s, i) => (
                <Link key={i} to={`/services?tab=${s.tab}`} className="block text-white/50 hover:text-[#C9A84C] transition-colors">
                  {s.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              {t('contact', 'title')}
            </h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#A68966] mt-0.5 flex-shrink-0" />
                <span className="text-white/50" data-testid="footer-address">{address}</span>
              </div>
              <a href={`tel:${phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-white/50 hover:text-[#C9A84C] transition-colors">
                <Phone className="w-4 h-4 text-[#A68966] flex-shrink-0" />
                <span data-testid="footer-phone">{phone}</span>
              </a>
              <a href={`mailto:${email}`} className="flex items-center gap-2 text-white/50 hover:text-[#C9A84C] transition-colors">
                <Mail className="w-4 h-4 text-[#A68966] flex-shrink-0" />
                <span data-testid="footer-email">{email}</span>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/40">
          <span>{t('footer', 'rights')}</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-[#C9A84C] transition-colors">{isAr ? 'سياسة الخصوصية' : 'Privacy Policy'}</Link>
            <Link to="/terms" className="hover:text-[#C9A84C] transition-colors">{isAr ? 'الشروط والأحكام' : 'Terms & Conditions'}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
